/* Environment Map — Share Modal */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ShareNetwork, X, Link, Copy, Trash, Eye } from '@phosphor-icons/react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export function ShareModal({ onClose }) {
  var [shares, setShares] = useState([]);
  var [creating, setCreating] = useState(false);

  useEffect(function () {
    fetch(API + '/map/shares', { credentials: 'include' })
      .then(function (r) { return r.ok ? r.json() : []; })
      .then(setShares)
      .catch(function () {});
  }, []);

  function createLink() {
    setCreating(true);
    fetch(API + '/map/share', { method: 'POST', credentials: 'include' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (data) {
          var newShare = { token: data.token, expires_at: data.expires_at, view_count: 0, created_at: new Date().toISOString() };
          setShares(function (prev) { return [newShare].concat(prev); });
          toast.success('Share link created');
        }
      })
      .catch(function () { toast.error('Failed to create link'); })
      .finally(function () { setCreating(false); });
  }

  function revokeLink(token) {
    fetch(API + '/map/shares/' + token, { method: 'DELETE', credentials: 'include' })
      .then(function (r) {
        if (r.ok) {
          setShares(function (prev) { return prev.filter(function (s) { return s.token !== token; }); });
          toast.success('Link revoked');
        }
      })
      .catch(function () {});
  }

  function copyUrl(token) {
    var url = window.location.origin + '/map/shared/' + token;
    navigator.clipboard.writeText(url).then(function () { toast.success('Copied to clipboard'); });
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="share-modal-backdrop">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} onClick={function (e) { e.stopPropagation(); }} className="bg-kova-card border border-kova-cyan/12 w-[420px] max-h-[480px] overflow-hidden" data-testid="share-modal">
        <div className="flex items-center justify-between px-4 py-3 border-b border-kova-cyan/12">
          <div className="flex items-center gap-2">
            <ShareNetwork size={14} className="text-kova-cyan" />
            <span className="text-sm font-heading font-bold">Share Map</span>
          </div>
          <button onClick={onClose} className="text-kova-slate hover:text-white"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">
          <Button onClick={createLink} disabled={creating} size="sm" className="w-full rounded-none bg-kova-cyan/15 text-kova-cyan border border-kova-cyan/30 hover:bg-kova-cyan/25 font-mono text-[10px] uppercase tracking-wider" data-testid="create-share-link-btn">
            <Link size={12} className="mr-1" />{creating ? 'Creating...' : 'Generate share link (30-day expiry)'}
          </Button>
          <div className="max-h-[300px] overflow-y-auto space-y-2">
            {shares.length === 0 && <p className="text-xs font-mono text-kova-slate text-center py-4">No active share links</p>}
            {shares.map(function (s) {
              return (
                <div key={s.token} className="flex items-center gap-2 px-3 py-2 bg-kova-bg border border-kova-cyan/12" data-testid={'share-link-' + s.token.slice(0, 8)}>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-mono text-kova-silv truncate">{s.token.slice(0, 16)}...</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-[8px] font-mono text-kova-slate">Expires: {new Date(s.expires_at).toLocaleDateString()}</span>
                      <span className="text-[8px] font-mono text-kova-slate flex items-center gap-0.5"><Eye size={8} />{s.view_count || 0} views</span>
                    </div>
                  </div>
                  <button onClick={function () { copyUrl(s.token); }} className="text-kova-slate hover:text-kova-cyan transition-colors" title="Copy URL"><Copy size={14} /></button>
                  <button onClick={function () { revokeLink(s.token); }} className="text-kova-slate hover:text-kova-red transition-colors" title="Revoke"><Trash size={14} /></button>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
